export type AdminTab = 'organizations' | 'signups' | 'revenue' | 'rate-plans' | 'subscriptions';

type OrgDetailSource = 'organizations' | 'signups';

export interface OrgDetailBackDestination {
  href: string;
  label: string;
}

export interface CardCollectionReturn {
  cardResult: 'collected' | 'cancelled';
  cleanUrl: string;
}

const DEFAULT_ADMIN_TAB: AdminTab = 'organizations';
const VALID_ADMIN_TABS = new Set<AdminTab>([
  'organizations',
  'signups',
  'revenue',
  'rate-plans',
  'subscriptions',
]);

export function resolveAdminTabFromSearch(search: string): AdminTab {
  const tab = new URLSearchParams(search).get('tab');
  return isAdminTab(tab) ? tab : DEFAULT_ADMIN_TAB;
}

export function getAdminDashboardUrl(tab: AdminTab): string {
  const params = new URLSearchParams();
  if (tab !== DEFAULT_ADMIN_TAB) {
    params.set('tab', tab);
  }
  return buildRelativeUrl('/admin', params);
}

export type OrgDetailInitialTab = 'overview' | 'users';

export function getOrgDetailUrl(
  orgId: string,
  options?: { from?: OrgDetailSource; tab?: 'users' }
): string {
  const path = `/admin/orgs/${encodeURIComponent(orgId)}`;
  const params = new URLSearchParams();
  if (options?.from === 'signups') {
    params.set('from', 'signups');
  }
  if (options?.tab === 'users') {
    params.set('tab', 'users');
  }
  return buildRelativeUrl(path, params);
}

/** The tab a detail page opens on: `?tab=users` right after creating an org (spec §7), Overview otherwise. */
export function resolveOrgDetailInitialTab(search: string): OrgDetailInitialTab {
  return new URLSearchParams(search).get('tab') === 'users' ? 'users' : 'overview';
}

export function getOrgDetailCardCollectionReturnPath(
  orgId: string,
  search: string
): string {
  const source = new URLSearchParams(search).get('from');
  return getOrgDetailUrl(
    orgId,
    source === 'signups' ? { from: 'signups' } : undefined
  );
}

export function resolveOrgDetailBackDestination(
  search: string
): OrgDetailBackDestination {
  const source = new URLSearchParams(search).get('from');
  if (source === 'signups') {
    return {
      href: getAdminDashboardUrl('signups'),
      label: 'Back to Signups',
    };
  }

  return {
    href: getAdminDashboardUrl('organizations'),
    label: 'Back to Organizations',
  };
}

export function resolveCardCollectionReturn(
  pathname: string,
  search: string
): CardCollectionReturn | null {
  const params = new URLSearchParams(search);
  const card = params.get('card');
  if (card !== 'collected' && card !== 'cancelled') {
    return null;
  }

  params.delete('card');
  params.delete('org');
  return {
    cardResult: card,
    cleanUrl: buildRelativeUrl(pathname, params),
  };
}

function isAdminTab(value: string | null): value is AdminTab {
  return value !== null && VALID_ADMIN_TABS.has(value as AdminTab);
}

function buildRelativeUrl(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query ? `${path}?${query}` : path;
}
